import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import Input from '../../components/ui/Input';
import Button from '../../components/ui/Button';
import { CONTACT } from '../../components/LegalDocument';

const TOPICS = [
  'Booking or payment',
  'Refund request',
  'Account or login',
  'Becoming a host',
  'Privacy or data request',
  'Safety concern',
  'Other',
];

const CHANNELS = [
  {
    icon: 'Mail',
    title: 'Email',
    value: CONTACT.email,
    href: `mailto:${CONTACT.email}`,
    note: 'We usually reply within 1–2 business days.',
  },
  {
    icon: 'Phone',
    title: 'Phone',
    value: CONTACT.phone,
    href: `tel:${CONTACT.phone.replace(/\s/g, '')}`,
    note: 'Monday to Friday, 9:00 am – 5:00 pm (Sri Lanka time).',
  },
  {
    icon: 'MapPin',
    title: 'Office',
    value: CONTACT.company,
    note: CONTACT.address,
  },
];

const FAQS = [
  {
    q: 'How do I cancel a booking or request a refund?',
    a: 'Open the booking in the KidQuest app to see its cancellation options. Refund eligibility follows our Terms & Conditions and the host’s policy for that activity. If you need help, contact us with your booking reference.',
  },
  {
    q: 'How do I delete my account?',
    a: 'In the app, go to Profile > Settings > Account Management > Delete Account. You can also email us and we will help. Deleting your account does not automatically cancel existing bookings.',
  },
  {
    q: 'How can I list my activities on KidQuest?',
    a: 'Choose “Becoming a host” in the form below and tell us about your organisation and activities. Our team will get in touch about eligibility and next steps.',
  },
  {
    q: 'How do I report a safety concern?',
    a: 'Choose “Safety concern” in the form below or call us directly. If anyone is in immediate danger, contact local emergency services first.',
  },
];

const EMPTY_FORM = { name: '', email: '', topic: TOPICS[0], reference: '', message: '' };

const validate = (form) => {
  const errors = {};
  if (!form.name.trim()) errors.name = 'Please enter your name';
  if (!form.email.trim()) errors.email = 'Please enter your email';
  else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) errors.email = 'Please enter a valid email';
  if (!form.message.trim()) errors.message = 'Please enter a message';
  return errors;
};

const Support = () => {
  const [form, setForm] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});
  const [sent, setSent] = useState(false);

  useEffect(() => {
    document.title = 'Support | KidQuest';
  }, []);

  const update = (field) => (e) => {
    setForm((prev) => ({ ...prev, [field]: e?.target?.value }));
    if (errors?.[field]) setErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    const nextErrors = validate(form);
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) return;

    const subject = `[Support] ${form.topic}${form.reference.trim() ? ` – ${form.reference.trim()}` : ''}`;
    const body = [
      `Name: ${form.name.trim()}`,
      `Email: ${form.email.trim()}`,
      `Topic: ${form.topic}`,
      ...(form.reference.trim() ? [`Booking reference: ${form.reference.trim()}`] : []),
      '',
      form.message.trim(),
    ].join('\n');

    window.location.href = `mailto:${CONTACT.email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    setSent(true);
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="border-b border-border bg-card/90 backdrop-blur sticky top-0 z-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <span className="text-xl font-semibold tracking-tight">
            Kid<span className="text-primary">Quest</span>
          </span>
          <a
            href={`mailto:${CONTACT.email}`}
            className="text-sm font-medium text-muted-foreground hover:text-primary transition-colors"
          >
            {CONTACT.email}
          </a>
        </div>
      </header>

      <div className="bg-muted border-b border-border">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10 sm:py-14">
          <p className="text-xs font-semibold uppercase tracking-widest text-primary mb-3">KidQuest / Support</p>
          <h1 className="text-3xl sm:text-4xl font-semibold mb-3">Contact us</h1>
          <p className="text-muted-foreground max-w-2xl">
            Questions about a booking, your account or hosting on KidQuest? Send us a message and our team will get back to you.
          </p>
        </div>
      </div>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-10">
        <div className="grid gap-4 sm:grid-cols-3 mb-12">
          {CHANNELS.map((c) => (
            <div key={c.title} className="rounded-lg border border-border bg-card p-5">
              <div className="w-10 h-10 rounded-md bg-primary/10 text-primary flex items-center justify-center mb-4">
                <Icon name={c.icon} size={20} />
              </div>
              <p className="text-sm text-muted-foreground mb-1">{c.title}</p>
              {c.href ? (
                <a href={c.href} className="font-medium text-primary hover:underline break-words">{c.value}</a>
              ) : (
                <p className="font-medium">{c.value}</p>
              )}
              <p className="text-sm text-muted-foreground mt-2">{c.note}</p>
            </div>
          ))}
        </div>

        <div className="lg:grid lg:grid-cols-[1fr_360px] lg:gap-12">
          <section className="rounded-lg border border-border bg-card p-6 sm:p-8 mb-10 lg:mb-0">
            <h2 className="text-xl font-semibold mb-1">Send us a message</h2>
            <p className="text-sm text-muted-foreground mb-6">
              Submitting opens your email app with the message ready to send to {CONTACT.email}.
            </p>

            {sent && (
              <div className="mb-6 rounded-lg border border-primary/20 bg-primary/5 p-4 flex gap-3">
                <Icon name="CheckCircle" size={20} className="text-primary shrink-0 mt-0.5" />
                <div className="text-sm leading-relaxed">
                  <p className="font-medium">Your email app should now be open.</p>
                  <p className="text-muted-foreground">
                    Press send there to reach us. If nothing opened, email us directly at{' '}
                    <a href={`mailto:${CONTACT.email}`} className="text-primary hover:underline">{CONTACT.email}</a>.
                  </p>
                </div>
              </div>
            )}

            <form onSubmit={handleSubmit} noValidate className="space-y-5">
              <div className="grid gap-5 sm:grid-cols-2">
                <Input id="support-name" label="Name" required value={form.name} onChange={update('name')} error={errors.name} autoComplete="name" />
                <Input id="support-email" label="Email" type="email" required value={form.email} onChange={update('email')} error={errors.email} autoComplete="email" />
              </div>

              <div className="grid gap-5 sm:grid-cols-2">
                <div className="space-y-2">
                  <label htmlFor="support-topic" className="text-sm font-medium leading-none">Topic</label>
                  <select
                    id="support-topic"
                    value={form.topic}
                    onChange={update('topic')}
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                  >
                    {TOPICS.map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <Input
                  id="support-reference"
                  label="Booking reference"
                  description="Optional"
                  value={form.reference}
                  onChange={update('reference')}
                />
              </div>

              <div className="space-y-2">
                <label
                  htmlFor="support-message"
                  className={`text-sm font-medium leading-none ${errors.message ? 'text-destructive' : ''}`}
                >
                  Message<span className="text-destructive ml-1">*</span>
                </label>
                <textarea
                  id="support-message"
                  rows={6}
                  value={form.message}
                  onChange={update('message')}
                  placeholder="Tell us how we can help"
                  className={`flex w-full rounded-md border bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 ${errors.message ? 'border-destructive focus-visible:ring-destructive' : 'border-input focus-visible:ring-ring'}`}
                />
                {errors.message && <p className="text-sm text-destructive">{errors.message}</p>}
              </div>

              <p className="text-xs text-muted-foreground">
                Please don’t include card numbers or other sensitive details. See our{' '}
                <Link to="/privacy-policy" className="text-primary hover:underline">Privacy Policy</Link>{' '}
                for how we handle your information.
              </p>

              <Button type="submit" iconName="Send" iconPosition="right" className="w-full sm:w-auto">
                Send message
              </Button>
            </form>
          </section>

          <aside>
            <h2 className="text-xl font-semibold mb-4">Common questions</h2>
            <div className="divide-y divide-border rounded-lg border border-border bg-card">
              {FAQS.map((f) => (
                <details key={f.q} className="group p-4">
                  <summary className="flex cursor-pointer list-none items-start justify-between gap-3 font-medium">
                    {f.q}
                    <Icon name="ChevronDown" size={18} className="shrink-0 mt-0.5 text-muted-foreground transition-transform group-open:rotate-180" />
                  </summary>
                  <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{f.a}</p>
                </details>
              ))}
            </div>
          </aside>
        </div>
      </main>

      <footer className="border-t border-border">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 flex flex-col sm:flex-row gap-3 justify-between text-sm text-muted-foreground">
          <span>© {new Date().getFullYear()} {CONTACT.company}</span>
          <div className="flex gap-6">
            <Link to="/terms-and-conditions" className="hover:text-primary">Terms & Conditions</Link>
            <Link to="/privacy-policy" className="hover:text-primary">Privacy Policy</Link>
            <Link to="/support" className="hover:text-primary">Support</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Support;
